import { z } from 'zod';
import { TargetSchema } from './domain.js';
import { MARKET_LANGUAGES } from './market-locales.js';
export const APPS_PATH = '/api/v1/apps';
export const LIBRARY_ITEMS_PATH = '/api/v1/library-items';
export const LISTING_SNAPSHOTS_PATH = '/api/v1/listing-snapshots';
export const STORE_LISTINGS_PATH = '/api/v1/store-listings';
export const INVENTORY_PATH = '/api/v1/inventory';
/**
 * What every Listing Snapshot is called wherever it is shown. Storefront extraction is
 * not Play Console truth and the words never soften into "sync".
 */
export const LISTING_SNAPSHOT_LABEL = 'чтение витрины';
export const APP_RESOURCE = 'app';
export const LISTING_SNAPSHOT_RESOURCE = 'listingSnapshot';
export const LIBRARY_ITEM_RESOURCE = 'libraryItem';
export const STORE_LISTING_RESOURCE = 'storeListing';
export const INVENTORY_SCAN_RESOURCE = 'inventoryScan';
/** An App exists in the Studio whether or not anything of it is on Google Play. */
export const AppPublicationSchema = z.enum(['published', 'manual']);
/**
 * What a piece of App Library material is for. Inferred from the bytes and the place
 * it came from, shown to the operator, and corrected by them when the guess is wrong.
 */
export const LibraryRoleSchema = z.enum([
    'productScreen',
    'logo',
    'brandAsset',
    'styleReference',
    'compositionReference',
    'sourceVideo',
    'competitorListing',
    'other',
]);
/** Where the material came from. Provenance is kept per App, bytes are not. */
export const LibraryProvenanceSchema = z.enum(['upload', 'url', 'listingImport']);
/**
 * `linkOnly` is not a failure to clear up. It is a Library item that is a link and
 * nothing else, and a Brief may select it exactly like one that has bytes.
 */
export const RetrievalStateSchema = z.enum(['pending', 'retrieved', 'linkOnly']);
export const AppResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    /** Null on a manually created App: publication is not a prerequisite. */
    packageName: z.string().nullable(),
    publication: AppPublicationSchema,
    displayName: z.string(),
    /** The address the operator pasted, kept even when nothing could be read from it. */
    sourceUrl: z.string().nullable(),
    defaultTarget: TargetSchema,
    /** App Profile: the standing creative constraints that apply to every Experiment. */
    standingConstraints: z.string(),
    positioning: z.string(),
    /**
     * Set while the App is in the archive: out of the Workspace, the Inventory and every
     * list an App is picked from, with everything it has kept.
     */
    archivedAt: z.string().nullable(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
/** Which listing placement an imported asset was shown in. */
export const ListingAssetRoleSchema = z.enum(['appIcon', 'featureGraphic', 'screenshot', 'previewVideo']);
export const ListingSnapshotAssetSchema = z.object({
    id: z.string().min(1),
    position: z.number().int().nonnegative(),
    role: ListingAssetRoleSchema,
    /** Null when the asset was seen on the page and its bytes could not be retrieved. */
    artifactId: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    warning: z.string().nullable(),
});
/**
 * The warning a reading taken from the page's markup carries: Play's machine translation is
 * recognised only in the page's data, so the `false` of such a reading was never observed.
 * Snapshots are immutable, so the sentence stays as stored readings already say it.
 */
export const MACHINE_TRANSLATION_NOT_RECOGNISED_WARNING = 'Страница прочитана из разметки, поэтому машинный перевод не распознан.';
/** Immutable once written. A later scan or review writes another one beside it. */
export const ListingSnapshotResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    /** Null only on a snapshot written before Store Listings existed and never assigned. */
    storeListingId: z.string().nullable(),
    /** The Inventory Scan that read it; null on a review or an older import. */
    inventoryScanId: z.string().nullable(),
    /** Play showed its own machine translation here, not text the developer wrote. */
    machineTranslated: z.boolean(),
    packageName: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    capturedAt: z.string().min(1),
    /** Set on the snapshot the operator reviewed, null on the one the import wrote. */
    reviewedAt: z.string().nullable(),
    /** The snapshot this one was reviewed out of, when there was one. */
    supersedesId: z.string().nullable(),
    label: z.literal(LISTING_SNAPSHOT_LABEL),
    locale: z.string().nullable(),
    market: z.string().nullable(),
    title: z.string().nullable(),
    shortDescription: z.string().nullable(),
    fullDescription: z.string().nullable(),
    developerName: z.string().nullable(),
    videoUrl: z.string().nullable(),
    warnings: z.array(z.string()),
    assets: z.array(ListingSnapshotAssetSchema),
});
export const LibraryItemResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    provenance: LibraryProvenanceSchema,
    sourceUrl: z.string().nullable(),
    originName: z.string().nullable(),
    role: LibraryRoleSchema,
    /** False once the operator has said what it is. */
    roleInferred: z.boolean(),
    retrievalState: RetrievalStateSchema,
    /** One line, never a stack trace. */
    retrievalWarning: z.string().nullable(),
    artifactId: z.string().nullable(),
    contentHash: z.string().nullable(),
    /** True while this item is standing App Profile material. */
    standing: z.boolean(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
/**
 * Either a pasted listing address or package name, or a name for an App that has
 * nothing on Play yet. Nothing else is required to start.
 */
export const CreateAppRequestSchema = z.object({
    paste: z.string().optional(),
    displayName: z.string().min(1).max(200).optional(),
    defaultTarget: TargetSchema.optional(),
});
export const AppCreatedResponseSchema = z.object({
    app: AppResourceSchema,
    /** The Inventory Scan that is now running, when there is a package to scan. */
    scanJobId: z.string().nullable(),
});
export const AppListResponseSchema = z.object({ apps: z.array(AppResourceSchema) });
/** The App Profile: reusable identity and standing constraints, all of it editable. */
export const UpdateAppProfileRequestSchema = z.object({
    displayName: z.string().min(1).max(200).optional(),
    defaultTarget: TargetSchema.optional(),
    standingConstraints: z.string().max(4000).optional(),
    positioning: z.string().max(4000).optional(),
});
export const AppProfileResponseSchema = z.object({
    app: AppResourceSchema,
    /** Library material marked as standing. Standing is still not selection. */
    standingMaterials: z.array(LibraryItemResourceSchema),
    latestSnapshot: ListingSnapshotResourceSchema.nullable(),
});
export const StandingMaterialRequestSchema = z.object({ libraryItemId: z.string().min(1) });
export const ListingSnapshotListResponseSchema = z.object({ snapshots: z.array(ListingSnapshotResourceSchema) });
/** One Inventory Scan Job per App asked for; `Scan all` asks for every published App. */
export const StartScanResponseSchema = z.object({ scanJobIds: z.array(z.string().min(1)) });
/** What a review is told when its replacement address is not one whole address. */
export const REPLACEMENT_ADDRESS_REFUSAL = 'Адрес замены должен быть одним полным адресом http или https, без пробелов и переносов строк, не длиннее 2000 символов.';
/**
 * One absolute http or https address and nothing else. A reading's assets are keyed line
 * by line, so a line break inside an address would read as another asset.
 */
function isWholeHttpAddress(value) {
    if (/\s/.test(value) || !/^https?:\/\//i.test(value))
        return false;
    try {
        return new URL(value).hostname !== '';
    }
    catch {
        return false;
    }
}
/**
 * The lightweight review. Removing is leaving an asset out, reordering is the order of
 * the list, replacing and supplementing are entries carrying an Artifact of their own -
 * or a fresh address for the preview video, which is only ever a YouTube link.
 * It writes a new snapshot; the imported one is never touched.
 */
export const ReviewListingSnapshotRequestSchema = z.object({
    assets: z.array(z.object({
        sourceAssetId: z.string().min(1).optional(),
        artifactId: z.string().min(1).optional(),
        sourceUrl: z.string().max(2000).refine(isWholeHttpAddress).optional(),
        role: ListingAssetRoleSchema,
    })),
    title: z.string().max(500).optional(),
    shortDescription: z.string().max(2000).optional(),
    fullDescription: z.string().max(20000).optional(),
});
export const LibraryListResponseSchema = z.object({ items: z.array(LibraryItemResourceSchema) });
/** The bytes arrive through the Artifact seam first; this puts them in the Library. */
export const RegisterLibraryItemRequestSchema = z.object({
    artifactId: z.string().min(1),
    role: LibraryRoleSchema.optional(),
    originName: z.string().max(500).optional(),
});
export const UpdateLibraryItemRequestSchema = z.object({ role: LibraryRoleSchema });
export const IngestReferenceRequestSchema = z.object({
    url: z.string().min(1).max(2000),
    role: LibraryRoleSchema.optional(),
});
export const IngestReferenceResponseSchema = z.object({
    item: LibraryItemResourceSchema,
    jobId: z.string().min(1),
});
/** The App's single Main Store Listing, or one of its Custom Store Listings. */
export const StoreListingKindSchema = z.enum(['main', 'custom']);
export const StoreListingResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    kind: StoreListingKindSchema,
    /** The `listing` value of a Custom Store Listing's URL: its identity inside the App. */
    listingParam: z.string().nullable(),
    /** How the operator calls it. A Custom Store Listing without a URL is only this. */
    name: z.string(),
    /** The storefront address, when the listing can be read at all. */
    url: z.string().nullable(),
    /**
     * The language a Custom Store Listing's own text is written in, as the operator says.
     * Play shows it untranslated to every language, so the storefront cannot tell; until
     * it is set nothing in it is judged a Translation Gap. Always null on the Main Store
     * Listing, whose language is what Play machine-translates it from.
     */
    language: z.string().nullable(),
    /** Set once the operator took it out of the inventory. Its history stays. */
    archivedAt: z.string().nullable(),
    createdAt: z.string().min(1),
});
/**
 * The language a Custom Store Listing is written in until the operator says otherwise:
 * every listing of the team starts out in English.
 */
export const DEFAULT_CUSTOM_LISTING_LANGUAGE = 'en';
/**
 * Custom Store Listings cannot be enumerated from outside Play Console, so the
 * operator names them: a pasted `&listing=` address, a bare `listing` value, or just a
 * name for one that has no URL and cannot be read. Named without a language, one is
 * written in the default one.
 */
export const RegisterStoreListingsRequestSchema = z.object({
    addresses: z.array(z.string().min(1).max(2000)).max(100).optional(),
    name: z.string().min(1).max(200).optional(),
    language: z.string().refine((value) => MARKET_LANGUAGES.includes(value), 'Это не язык Market Locale.').optional(),
});
export const SetStoreListingLanguageRequestSchema = z.object({
    language: z.string().refine((value) => MARKET_LANGUAGES.includes(value), 'Это не язык Market Locale.'),
});
export const RegisterStoreListingsResponseSchema = z.object({
    listings: z.array(StoreListingResourceSchema),
    /** Addresses that were not a Custom Store Listing of this App, each with the reason. */
    refused: z.array(z.object({ entered: z.string(), problem: z.string() })),
});
export const InventoryScanStateSchema = z.enum(['reading', 'finished', 'stopped']);
export const InventoryScanResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    /** The one Store Listing this scan read; null when it read every one of the App. */
    storeListingId: z.string().nullable(),
    state: InventoryScanStateSchema,
    /** Market Locales across every readable Store Listing this scan set out to read. */
    planned: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    missed: z.number().int().nonnegative(),
    /** One line saying why the scan stopped before reading everything. */
    stopReason: z.string().nullable(),
    /** The Main Store Listing answered `not found`: the App is not on Google Play. */
    notOnPlay: z.boolean(),
    startedAt: z.string().min(1),
    finishedAt: z.string().nullable(),
});
/**
 * What one Store Listing shows in one Market Locale, judged against its default
 * language: `gap` is a Translation Gap, `unread` was never read successfully and is
 * never counted as a gap.
 */
export const InventoryCellStateSchema = z.enum(['unread', 'default', 'translated', 'gap']);
export const InventoryCellSchema = z.object({
    market: z.string().min(2),
    locale: z.string().min(2),
    state: InventoryCellStateSchema,
    /** Play is showing its own machine translation here. */
    machineTranslated: z.boolean(),
    /** Screenshots and feature graphic differ from the default language's; null when unread. */
    graphicsLocalized: z.boolean().nullable(),
    /** Differs from the reading before this one of the same Market Locale. */
    changed: z.boolean(),
    snapshotId: z.string().nullable(),
    /**
     * The reading before this one of the same Market Locale, by id: a comparison opens
     * both readings by their ids. Null when there is no reading before this one.
     */
    previousSnapshotId: z.string().nullable(),
    capturedAt: z.string().nullable(),
});
export const InventoryListingSchema = z.object({
    storeListing: StoreListingResourceSchema,
    /** The Play language whose content every other one is compared with; null until known. */
    defaultLocale: z.string().nullable(),
    /** Every Market Locale, in the order of the market table. Empty for an unreadable listing. */
    cells: z.array(InventoryCellSchema),
    /** Its content equals the Main Store Listing's everywhere it was read. */
    possiblyRetired: z.boolean(),
    /** Its own latest scan, while that is newer than the App's; null otherwise. */
    latestScan: InventoryScanResourceSchema.nullable(),
    /** A scan that reads it is queued or running: its own, or its App's. */
    scanPending: z.boolean(),
});
export const InventoryAppSchema = z.object({
    app: AppResourceSchema,
    listings: z.array(InventoryListingSchema),
    latestScan: InventoryScanResourceSchema.nullable(),
    /** A scan is queued or running: scans take turns, so it may be waiting for another App's. */
    scanPending: z.boolean(),
});
export const InventoryResponseSchema = z.object({
    apps: z.array(InventoryAppSchema),
    /** The published Apps in the archive, by name: the Inventory lists them only to bring one back. */
    archived: z.array(AppResourceSchema),
});
//# sourceMappingURL=app.js.map